import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { E2Ntable, E2NtableHeader, E2NtableRow } from '../../components/Table';
import { useEffect, useState } from 'react';
import { Abteilung, Antrag, Arbeitsbereich, Arbeitszeit } from './entities';
import {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@/components/ui/table';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { NoDataFallback } from '@/domain/arbeitszeiten/shared/components/NoDataFallback';

export type ArbeitsbereichFilter = {
  arbeitsbereiche?: string[];
  abteilungen?: string[];
};

export type DataTableRow = Arbeitszeit | Antrag;

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  nameFilter: string;
  arbeitsbereichFilter: Arbeitsbereich[];
  abteilungFilter: Abteilung[];
}

// eslint-disable-next-line max-lines-per-function
export function DataTable({
  columns,
  data,
  nameFilter,
  arbeitsbereichFilter,
  abteilungFilter,
}: DataTableProps<DataTableRow, DataTableRow[]>) {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([
    { id: 'mitarbeiter', value: nameFilter },
  ]);
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'arbeitszeit', desc: false },
  ]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    state: { columnFilters, sorting },
    onSortingChange: setSorting,
  });
  const { currentStandort } = useStandortContext();

  useEffect(() => {
    const mitarbeiterColumn = table.getColumn('mitarbeiter');
    const arbeitsbereichColumn = table.getColumn('arbeitsbereich');

    if (mitarbeiterColumn) {
      mitarbeiterColumn.setFilterValue(nameFilter);
    }

    if (arbeitsbereichColumn) {
      const filterValue = {
        arbeitsbereiche: arbeitsbereichFilter.map(
          (arbeitsbereich) => arbeitsbereich.id,
        ),
        abteilungen: currentStandort.abteilungenEnabled
          ? abteilungFilter.map((abteilung) => abteilung.id)
          : [],
      };
      arbeitsbereichColumn.setFilterValue(filterValue);
    }
  }, [
    nameFilter,
    arbeitsbereichFilter,
    abteilungFilter,
    currentStandort.abteilungenEnabled,
    table,
  ]);

  return (
    <E2Ntable>
      <E2NtableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow className="border-b-0" key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              return (
                <TableHead
                  className={'px-0 text-xs normal-case font-semibold h-0 pt-4'}
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </E2NtableHeader>
      <TableBody>
        {table.getSortedRowModel().rows?.length ? (
          table.getSortedRowModel().rows.map((row) => {
            return (
              <E2NtableRow
                className={`hover:bg-transparent h-16 ${
                  'abwesenheit' in row.original &&
                  row.original.abwesenheit !== null
                    ? '[&_td]:bg-white/50'
                    : '[&_td]:bg-white'
                }`}
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}>
                {row
                  .getVisibleCells()
                  .map((cell) =>
                    flexRender(cell.column.columnDef.cell, cell.getContext()),
                  )}
              </E2NtableRow>
            );
          })
        ) : (
          <TableRow>
            <TableCell colSpan={columns.length} className="h-24 text-center">
              <NoDataFallback />
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </E2Ntable>
  );
}
